/**
 * Procedures Management helpers: paths, React Query keys, friendly error messages (the API's error
 * codes arrive in errors[0]), stamps (yyyyMMddHHmmss numbers already localised to the caller),
 * durations, file sizes and downloads.
 *
 * All copy here is South African English with no long dashes.
 */

import {
  PROCEDURES_STATUS,
  PROCEDURES_VISIBILITY,
  PROCEDURES_AUDIENCE_TYPE,
  PROCEDURES_CALLOUT_TYPE,
  PROCEDURES_MEDIA_TYPE,
  PROCEDURES_RUN_STATUS,
  PROCEDURES_RUN_STEP_STATUS,
  PROCEDURES_RUN_CHANNEL,
  PROCEDURES_IMPORT_STATUS,
  PROCEDURES_DOC_INDEX_STATUS,
  PROCEDURES_COLLABORATOR_LEVEL,
  PROCEDURES_ERROR_CODES,
  PROCEDURES_FILE_DOMAIN,
} from '@actschurch/ccms-sdk';
import { stampToDate } from '../../FormsManagement/components/formsUtils';
import { relativeTime as formsRelativeTime } from '../../FormsManagement/components/formsDisplay';

export {
  PROCEDURES_STATUS,
  PROCEDURES_VISIBILITY,
  PROCEDURES_AUDIENCE_TYPE,
  PROCEDURES_CALLOUT_TYPE,
  PROCEDURES_MEDIA_TYPE,
  PROCEDURES_RUN_STATUS,
  PROCEDURES_RUN_STEP_STATUS,
  PROCEDURES_RUN_CHANNEL,
  PROCEDURES_IMPORT_STATUS,
  PROCEDURES_DOC_INDEX_STATUS,
  PROCEDURES_COLLABORATOR_LEVEL,
  PROCEDURES_ERROR_CODES,
  PROCEDURES_FILE_DOMAIN,
};
export { stampToDate };
export { colorFor, initials } from '../../FormsManagement/components/formsDisplay';

// ---------------------------------------------------------------- paths

export const PROCEDURES_BASE_PATH = '/app/procedures';

export const proceduresPaths = {
  dashboard: PROCEDURES_BASE_PATH,
  list: `${PROCEDURES_BASE_PATH}/list`,
  import: `${PROCEDURES_BASE_PATH}/import`,
  runs: `${PROCEDURES_BASE_PATH}/runs`,
  problems: `${PROCEDURES_BASE_PATH}/problems`,
  categories: `${PROCEDURES_BASE_PATH}/categories`,
  settings: `${PROCEDURES_BASE_PATH}/settings`,
  qrSheet: (q: { categoryId?: number | null; locationId?: number | null; ids?: number[] } = {}) => {
    const params = new URLSearchParams();
    if (q.categoryId != null) params.set('categoryId', String(q.categoryId));
    if (q.locationId != null) params.set('locationId', String(q.locationId));
    if (q.ids && q.ids.length > 0) params.set('ids', q.ids.join(','));
    const s = params.toString();
    return `${PROCEDURES_BASE_PATH}/qr-sheet${s ? `?${s}` : ''}`;
  },
  editor: (id: number | string, tab?: string) => `${PROCEDURES_BASE_PATH}/${id}${tab && tab !== 'details' ? `?tab=${tab}` : ''}`,
};

// ---------------------------------------------------------------- query keys

export const proceduresKeys = {
  all: ['procedures'] as const,
  dashboard: () => ['procedures', 'dashboard'] as const,
  lists: () => ['procedures', 'list'] as const,
  list: (q: unknown) => ['procedures', 'list', q] as const,
  detail: (id: number) => ['procedures', 'detail', id] as const,
  preview: (id: number) => ['procedures', 'preview', id] as const,
  versions: (id: number) => ['procedures', 'versions', id] as const,
  qr: (id: number) => ['procedures', 'qr', id] as const,
  access: (id: number) => ['procedures', 'access', id] as const,
  documents: (id: number) => ['procedures', 'documents', id] as const,
  questions: (id: number, page: number) => ['procedures', 'questions', id, page] as const,
  imports: (page: number) => ['procedures', 'imports', page] as const,
  importItem: (id: number) => ['procedures', 'import', id] as const,
  runsRoot: () => ['procedures', 'runs'] as const,
  runs: (q: unknown) => ['procedures', 'runs', q] as const,
  run: (id: number) => ['procedures', 'run', id] as const,
  problemsRoot: () => ['procedures', 'problems'] as const,
  problems: (q: unknown) => ['procedures', 'problems', q] as const,
  categories: () => ['procedures', 'categories'] as const,
  settings: () => ['procedures', 'settings'] as const,
  lookups: () => ['procedures', 'lookups'] as const,
  ministries: () => ['procedures', 'ministries'] as const,
  qrSheet: (q: unknown) => ['procedures', 'qr-sheet', q] as const,
};

// ---------------------------------------------------------------- errors

const ERROR_COPY: Record<string, string> = {
  [PROCEDURES_ERROR_CODES.MediaStorageUnavailable]:
    "Media uploads aren't set up on this server yet. Ask an administrator to configure the private media store.",
  [PROCEDURES_ERROR_CODES.AiUnavailable]:
    "The AI service is switched off or can't be reached right now. Try again later or ask an administrator to switch it on.",
  [PROCEDURES_ERROR_CODES.UnsupportedFile]:
    "That file type isn't supported. Use one PDF, Word (DOCX), text or Markdown file, or photos (JPEG, PNG or WebP).",
  [PROCEDURES_ERROR_CODES.NothingToImport]: 'There was nothing to import. Add a file, some photos or paste the text first.',
  [PROCEDURES_ERROR_CODES.NotPublished]: "This procedure hasn't been published yet.",
  [PROCEDURES_ERROR_CODES.SignInRequired]: 'This procedure is private: people need to sign in to open it.',
  [PROCEDURES_ERROR_CODES.NotAssigned]: "You aren't assigned to this procedure.",
  [PROCEDURES_ERROR_CODES.PublicRunsDisabled]: 'Public runs are switched off in the Procedures settings.',
  [PROCEDURES_ERROR_CODES.RunTokenInvalid]: 'That run link has expired.',
  [PROCEDURES_ERROR_CODES.RunClosed]: 'That run has already finished.',
  [PROCEDURES_ERROR_CODES.ChatDisabled]: 'The AI chat is switched off for this procedure.',
  [PROCEDURES_ERROR_CODES.ChatRateLimited]: 'Too many questions in a short time. Try again in a while.',
  [PROCEDURES_ERROR_CODES.RecaptchaFailed]: 'The robot check failed. Try again.',
};

/** The Procedures error code in errors[0], if any. */
export function proceduresErrorCode(err: unknown): string | undefined {
  const e = err as { errors?: unknown } | null;
  if (e && Array.isArray(e.errors) && typeof e.errors[0] === 'string') return e.errors[0] as string;
  return undefined;
}

/** HTTP status of an SDK error (CCMSError.status), if any. */
export function proceduresErrorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; statusCode?: number } | null;
  return e?.status ?? e?.statusCode ?? undefined;
}

/** A friendly message for an SDK error: known error codes first, then the API message. */
export function proceduresErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const code = proceduresErrorCode(err);
  if (code && ERROR_COPY[code]) return ERROR_COPY[code];
  const status = proceduresErrorStatus(err);
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That item couldn't be found. It may have been deleted.";
  const e = err as { message?: string; errors?: unknown };
  const details = Array.isArray(e.errors)
    ? (e.errors as unknown[]).filter((x): x is string => typeof x === 'string' && !/^[a-z_]+$/.test(x))
    : [];
  const base = e.message || fallback;
  return details.length ? `${base}: ${details.join('; ')}` : base;
}

// ---------------------------------------------------------------- stamps and dates

export function formatStampDate(stamp: number | string | null | undefined, fallback = 'Not set'): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : fallback;
}

export function formatStampDateTime(stamp: number | string | null | undefined, fallback = 'Not set'): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : fallback;
}

export function formatStampTime(stamp: number | string | null | undefined, fallback = ''): string {
  const d = stampToDate(stamp);
  return d ? d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' }) : fallback;
}

export function relativeStamp(stamp: number | string | null | undefined, fallback = 'Never'): string {
  return formsRelativeTime(stamp, fallback);
}

const pad = (n: number) => String(n).padStart(2, '0');

/** Date to yyyy-MM-dd (local). */
export function dateToYmd(date: Date | null | undefined): string | undefined {
  if (!date || Number.isNaN(date.getTime())) return undefined;
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** yyyy-MM-dd to a local Date. */
export function ymdToDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const s = value.replace(/-/g, '');
  if (s.length < 8) return null;
  const d = new Date(Number(s.slice(0, 4)), Number(s.slice(4, 6)) - 1, Number(s.slice(6, 8)));
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatYmd(value: string | null | undefined, fallback = 'Not set'): string {
  const d = ymdToDate(value);
  return d ? d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : fallback;
}

/** 95 to "1 min 35 s", 3720 to "1 h 2 min". */
export function formatDuration(seconds: number | null | undefined, fallback = 'Not finished'): string {
  if (seconds === null || seconds === undefined || seconds < 0) return fallback;
  const s = Math.round(seconds);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rest = s % 60;
    return rest ? `${m} min ${rest} s` : `${m} min`;
  }
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h} h ${mm} min` : `${h} h`;
}

// ---------------------------------------------------------------- files

/** "820 KB", "1.4 MB" */
export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return 'Size unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The MIME type to send for a file (browsers leave .md empty). */
export function mimeOf(file: File): string {
  if (file.type) return file.type;
  const name = file.name.toLowerCase();
  if (name.endsWith('.md') || name.endsWith('.markdown')) return 'text/markdown';
  if (name.endsWith('.txt')) return 'text/plain';
  if (name.endsWith('.pdf')) return 'application/pdf';
  if (name.endsWith('.docx')) return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

export const isImageFile = (file: File) => mimeOf(file).startsWith('image/');
export const isVideoFile = (file: File) => mimeOf(file).startsWith('video/');
export const isAudioFile = (file: File) => mimeOf(file).startsWith('audio/');

/** Save a Blob as a file. */
export function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Download a URL as a file; falls back to opening it in a new tab when the browser blocks the fetch. */
export async function downloadUrl(url: string, filename: string) {
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(String(res.status));
    downloadBlob(await res.blob(), filename);
  } catch {
    window.open(url, '_blank', 'noopener');
  }
}

/** "Kids Church Sound System" to "kids-church-sound-system". */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'procedure';
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
